"use client";

// Ported from the pre-block private transcript's `tool-group.tsx`
// (a89c97b0c, plan-024 follow-up): consecutive tool calls condense into one
// Magnus-headed collapsible group with a rolling preview while streaming,
// standalone tools (calendar/mail cards) break the chain, and reasoning
// folds into the chain as an in-timeline "thinking" row. Two deliberate
// deltas, same as the block's other ports:
//   1. Kind/category come from the host-injected `ToolUIRegistry` (the
//      block's `toolViews` seam) instead of the private static registry.
//   2. Part typing is the block-local structural `ToolPartLike`.
import { c as _c } from "react/compiler-runtime";
import type { MagnusUIMessage, ToolCategory } from "@bg-bgi/magnus-sdk";
import type { ToolUIRegistry } from "@bg-bgi/magnus-sdk/react";
import { Reasoning, ReasoningContent, ReasoningTrigger } from "@workspace/ui/components/ai-elements/reasoning";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@workspace/ui/components/collapsible";
import { cn } from "@workspace/ui/lib/utils";
import { Ban, ChevronsUpDown, Lightbulb } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { memo, use, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MagnusLogo } from "./magnus-logo";
import { isToolPart, ToolPart, type ToolPartLike } from "./tool-part";
import { TimelineContext, ToolItem, toolIconClass } from "./tools/_framework/tool-item";

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

type MessagePart = MagnusUIMessage["parts"][number];
type ReasoningPart = Extract<MessagePart, {
  type: "reasoning";
}>;

/**
 * One link in a tool chain. Reasoning ("thinking") is an in-chain item
 * alongside tool calls so it condenses into the same Magnus wrapper as the
 * tools around it instead of breaking the chain into a separate block.
 */
export type ToolGroupItem = {
  kind: "tool";
  part: ToolPartLike;
  index: number;
} | {
  kind: "reasoning";
  part: ReasoningPart;
  index: number;
};
export type PartSegment = {
  type: "text";
  part: Extract<MessagePart, {
    type: "text";
  }>;
  index: number;
} | {
  type: "tool-group";
  items: ToolGroupItem[];
} | {
  type: "compaction-summary" | "compaction-continuation" | "part";
  part: MessagePart;
  index: number;
};

/** Compaction wire literals (ADR-054), matched structurally — same idiom as
 *  message.tsx, duplicated so tool-group never imports its consumer. */
const isCompactionSummaryPart = (part: MessagePart) => part.type === "data-compaction-summary";
const isCompactionContinuationPart = (part: MessagePart) => part.type === "data-compaction-continuation";

/**
 * Progressive-disclosure proxy wire name (the server's
 * `PROGRESSIVE_DISCLOSURE_TOOL_NAMES.call`), matched structurally so the
 * block stays host-agnostic.
 */
const PD_CALL_TOOL_NAME = "callCapabilityTool";

/**
 * The tool name that drives grouping/summary. Progressive-disclosure tool
 * calls arrive on the *live stream* as the generic `callCapabilityTool`
 * proxy — their inner identity is only synthesized at render time or
 * rewritten on persist. Grouping by the bare proxy name would fold a
 * standalone inner tool (e.g. a skill / artifact card) into the collapsible
 * chain until the chat reloads. So when the part is the proxy, recover the
 * inner tool's namespaced name (`${capabilityId}_${toolName}`) from its
 * input (present as soon as args stream in) or, failing that, its output
 * envelope.
 */
function effectiveToolName(part: ToolPartLike): string {
  const bare = part.type === "dynamic-tool" && part.toolName ? part.toolName : part.type.startsWith("tool-") ? part.type.slice("tool-".length) : part.type;
  if (bare !== PD_CALL_TOOL_NAME) {
    return bare;
  }
  const input = part.input as {
    capabilityId?: unknown;
    toolName?: unknown;
  } | undefined;
  if (typeof input?.capabilityId === "string" && input.capabilityId && typeof input?.toolName === "string" && input.toolName) {
    return `${input.capabilityId}_${input.toolName}`;
  }
  const output = part.output as {
    innerCapabilityId?: unknown;
    innerToolName?: unknown;
  } | undefined;
  if (typeof output?.innerCapabilityId === "string" && output.innerCapabilityId && typeof output?.innerToolName === "string" && output.innerToolName) {
    return `${output.innerCapabilityId}_${output.innerToolName}`;
  }
  return bare;
}
const isStandalone = (part: ToolPartLike, registry: ToolUIRegistry) => registry[effectiveToolName(part)]?.kind === "standalone";

/** A standalone tool closes off a group — nothing can merge in after it. */
const closesGroup = (item: ToolGroupItem | undefined, registry: ToolUIRegistry): boolean => !!item && item.kind === "tool" && isStandalone(item.part, registry);
export function groupMessageParts(parts: MessagePart[], registry: ToolUIRegistry): PartSegment[] {
  const segments: PartSegment[] = [];
  const appendItem = (item: ToolGroupItem, canMerge: boolean) => {
    const last = segments.at(-1);
    if (canMerge && last?.type === "tool-group") {
      last.items.push(item);
    } else {
      segments.push({
        type: "tool-group",
        items: [item]
      });
    }
  };
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!part) {
      continue;
    }
    if (part.type === "text") {
      segments.push({
        type: "text",
        part: part as Extract<MessagePart, {
          type: "text";
        }>,
        index: i
      });
      continue;
    }
    if (part.type === "reasoning") {
      // Reasoning folds into the current chain (or starts one) rather than
      // breaking tool grouping — so thinking condenses into the wrapper with
      // the tools around it. It still can't attach after a standalone tool.
      const last = segments.at(-1);
      const canMerge = last?.type === "tool-group" && !closesGroup(last.items.at(-1), registry);
      appendItem({
        kind: "reasoning",
        part: part as ReasoningPart,
        index: i
      }, canMerge);
      continue;
    }
    if (isCompactionSummaryPart(part)) {
      segments.push({
        type: "compaction-summary",
        part,
        index: i
      });
      continue;
    }
    if (isCompactionContinuationPart(part)) {
      segments.push({
        type: "compaction-continuation",
        part,
        index: i
      });
      continue;
    }
    if (!isToolPart(part)) {
      // Chips (skill/artifact references) render real inline UI, so they keep
      // their own segment exactly where they arrived. Everything else that
      // lands here — `step-start` stream boundaries, unknown `data-*` parts —
      // renders NOTHING in `Part` (message.tsx); emitting a segment for an
      // invisible part would split the surrounding tool chain for no reason
      // (pre-024 skipped these in `groupMessageParts` outright). Only text
      // and standalone tools break a group. (The union is exhausted here, so
      // `part` is `never` — read the tag via a widened cast.)
      const partType = (part as {
        type: string;
      }).type;
      if (partType === "data-skill-reference" || partType === "data-artifact-reference" || partType === "data-voice-session") {
        segments.push({
          type: "part",
          part,
          index: i
        });
      }
      continue;
    }

    // Standalone tools always start a fresh segment; a step tool also
    // can't merge into a group whose trailing item was standalone.
    const last = segments.at(-1);
    const canMerge = last?.type === "tool-group" && !isStandalone(part, registry) && !closesGroup(last.items.at(-1), registry);
    appendItem({
      kind: "tool",
      part,
      index: i
    }, canMerge);
  }
  return segments;
}

/**
 * Index of the tool group that should render the "Working" state, or `-1`.
 * The active group is the trailing tool-group with no real text/reasoning
 * after it. Walks backward once — O(n).
 */
export function findActiveToolGroupIndex(segments: PartSegment[]): number {
  for (let i = segments.length - 1; i >= 0; i--) {
    const segment = segments[i];
    if (!segment) {
      continue;
    }
    if (segment.type === "tool-group") {
      return i;
    }
    // Compaction boundary marks a hard cut — any prior tool group is settled.
    // Chips/unknown parts are real content — they settle the previous group.
    if (segment.type !== "text") {
      return -1;
    }
    // Empty text/reasoning parts can stream in before any token arrives —
    // skip them; only real content deactivates the previous tool group.
    if (segment.part.text.trim().length > 0) {
      return -1;
    }
  }
  return -1;
}

// ---------------------------------------------------------------------------
// Summary — a category-driven phrase like "Did some research and ran 3 commands".
// Tallies by category (not chronology), renders in a fixed order, caps at 3.
// ---------------------------------------------------------------------------

const leadCase = (phrase: string, leading: boolean): string => leading ? phrase : phrase.charAt(0).toLowerCase() + phrase.slice(1);
type CategoryFormatter = (count: number, leading: boolean) => string;
const CATEGORY_FORMATTER: Record<ToolCategory, CategoryFormatter> = {
  research: (_n, lead) => leadCase("Did some research", lead),
  web: (n, lead) => leadCase(n === 1 ? "Searched the web" : `Made ${n} web searches`, lead),
  code: (n, lead) => leadCase(n === 1 ? "Ran a command" : `Ran ${n} commands`, lead),
  create: (n, lead) => leadCase(n === 1 ? "Created an asset" : `Created ${n} assets`, lead),
  message: (n, lead) => leadCase(n === 1 ? "Sent a message" : `Sent ${n} messages`, lead),
  memory: (_n, lead) => leadCase("Updated memory", lead),
  weather: (_n, lead) => leadCase("Checked the weather", lead),
  metrics: (n, lead) => leadCase(n === 1 ? "Pulled metrics" : `Pulled ${n} metric reports`, lead)
};
const CATEGORY_ORDER: readonly ToolCategory[] = ["research", "web", "code", "create", "message", "memory", "weather", "metrics"];
const MAX_SUMMARY_PHRASES = 3;
function joinPhrases(phrases: string[]): string {
  if (phrases.length === 0) {
    return "";
  }
  if (phrases.length === 1) {
    return phrases[0] ?? "";
  }
  if (phrases.length === 2) {
    return `${phrases[0]} and ${phrases[1]}`;
  }
  return `${phrases.slice(0, -1).join(", ")}, and ${phrases.at(-1) ?? ""}`;
}
function summarizeToolGroup(parts: ToolPartLike[], registry: ToolUIRegistry): string {
  if (parts.length === 0) {
    return "";
  }
  const counts = new Map<ToolCategory, number>();
  for (const part of parts) {
    const category = registry[effectiveToolName(part)]?.category ?? "code" as const;
    counts.set(category, (counts.get(category) ?? 0) + 1);
  }
  const phrases: string[] = [];
  for (const category of CATEGORY_ORDER) {
    const count = counts.get(category);
    if (!count) {
      continue;
    }
    phrases.push(CATEGORY_FORMATTER[category](count, phrases.length === 0));
  }
  if (phrases.length <= MAX_SUMMARY_PHRASES) {
    return joinPhrases(phrases);
  }

  // Overflow → keep the first (MAX-1) phrases, condense the rest.
  const kept = phrases.slice(0, MAX_SUMMARY_PHRASES - 1);
  kept.push(`${phrases.length - kept.length} more things`);
  return joinPhrases(kept);
}

// ---------------------------------------------------------------------------
// Connector — 8px spine segment between two stacked rows. Used both inside
// the timeline (between tools) and at the top of each rolling-stack item
// (linking the Magnus header above to the first tool).
// ---------------------------------------------------------------------------

function Connector() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className="flex h-2"><div className="flex w-5 justify-center"><div className="h-full w-px bg-border" /></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// ---------------------------------------------------------------------------
// ToolTimeline — full vertical list of tool calls with auto-connecting spine.
// Used both standalone (single-tool / fully-expanded group) and as the
// expanded contents of CollapsibleToolGroup.
// ---------------------------------------------------------------------------

type ToolTimelineProps = {
  items: ToolGroupItem[];
  isStreamActive: boolean;
};
function isPendingToolPart(part: ToolPartLike): boolean {
  return part.state === "input-streaming" || part.state === "input-available";
}

// ---------------------------------------------------------------------------
// ReasoningTimelineItem — the in-chain "thinking" row. Reuses the shared
// `Reasoning` block so it keeps the exact live behavior: auto-opens while
// streaming and shows the reasoning text clamped to a rolling window (with
// auto-scroll + top fade), then auto-closes a second after the stream ends
// (a historical row mounts closed). Wrapped in the timeline's icon gutter +
// spine so it threads through the chain like a tool row instead of getting
// its own block.
// ---------------------------------------------------------------------------

function ReasoningTimelineItem({
  text,
  isStreaming
}: {
  text: string;
  isStreaming: boolean;
}) {
  const timeline = use(TimelineContext);
  const showLine = !!timeline?.isInTimeline && !timeline?.isLast;

  // Register with the timeline so the connector spine threads through this
  // reasoning row just like a tool row does.
  const registerRef = useRef(timeline?.register);
  registerRef.current = timeline?.register;
  useLayoutEffect(() => {
    registerRef.current?.();
  }, []);
  const hasText = !!text.trim();
  return <div className="flex min-w-0">
      <div className="flex w-5 shrink-0 flex-col items-center">
        <span className="flex h-7 items-center justify-center">
          <Lightbulb className={toolIconClass} />
        </span>
        {showLine && <span className="w-px flex-1 bg-border" />}
      </div>
      <div className="min-w-0 flex-1 pl-1.5">
        <Reasoning defaultOpen={hasText} hasContent={hasText} isStreaming={isStreaming}>
          <ReasoningTrigger className="h-7" />
          {hasText && <ReasoningContent>{text}</ReasoningContent>}
        </Reasoning>
      </div>
    </div>;
}

/** Renders a single chain item (tool or reasoning) inside a timeline slot. */
function TimelineItem(t0) {
  const $ = _c(10);
  const {
    item,
    isStreamActive
  } = t0;
  if (item.kind === "reasoning") {
    const t1 = isStreamActive && (item.part as {
      state?: string;
    }).state === "streaming";
    let t2;
    if ($[0] !== item.part.text || $[1] !== t1) {
      t2 = <ReasoningTimelineItem isStreaming={t1} text={item.part.text} />;
      $[0] = item.part.text;
      $[1] = t1;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] !== isStreamActive || $[4] !== item.part) {
    t1 = !isStreamActive && isPendingToolPart(item.part);
    $[3] = isStreamActive;
    $[4] = item.part;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const isCancelled = t1;
  let t2;
  if ($[6] !== isCancelled || $[7] !== isStreamActive || $[8] !== item.part) {
    t2 = isCancelled ? <ToolItem icon={<Ban className={toolIconClass} />} label="Cancelled" /> : <ToolPart part={item.part} streaming={isStreamActive} />;
    $[6] = isCancelled;
    $[7] = isStreamActive;
    $[8] = item.part;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export function ToolTimeline(t0) {
  const $ = _c(6);
  const {
    items,
    isStreamActive
  } = t0;
  const [connected, setConnected] = useState(_temp);
  let t1;
  if ($[0] !== connected || $[1] !== isStreamActive || $[2] !== items) {
    t1 = items.map((item, i) => {
      const {
        index
      } = item;
      const previous = items[i - 1];
      const next = items[i + 1];
      const isConnected = connected.has(index);
      const prevConnected = previous ? connected.has(previous.index) : false;
      const nextConnected = next ? connected.has(next.index) : false;
      const showConnector = isConnected && prevConnected;
      const isLastConnected = !nextConnected;
      return <div key={index}>{showConnector && <Connector />}<TimelineContext.Provider value={{
          isInTimeline: true,
          isLast: isLastConnected,
          register: () => setConnected(prev => {
            if (prev.has(index)) {
              return prev;
            }
            const nextSet = new Set(prev);
            nextSet.add(index);
            return nextSet;
          })
        }}><TimelineItem isStreamActive={isStreamActive} item={item} /></TimelineContext.Provider></div>;
    });
    $[0] = connected;
    $[1] = isStreamActive;
    $[2] = items;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <div>{t1}</div>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

// ---------------------------------------------------------------------------
// CollapsibleToolGroup — Magnus header that's always present while the model
// is streaming, with a rolling preview of the last few tools beneath it.
// Click anywhere on the header to expand into the full timeline.
// ---------------------------------------------------------------------------
function _temp() {
  return new Set();
}
const STACK_SIZE = 3;
const NOOP = () => undefined;
const STACK_TRANSITION = {
  type: "spring",
  stiffness: 360,
  damping: 30,
  mass: 0.7,
  opacity: {
    duration: 0.22,
    ease: "easeOut"
  }
} as const;
const PREVIEW_TRANSITION = {
  duration: 0.28,
  ease: [0.25, 0.1, 0.25, 1]
} as const;

// Mutable on purpose — Motion expects keyframe arrays it can mutate.
const LOGO_PULSE = {
  scale: [1, 1.08, 1],
  opacity: [0.85, 1, 0.85]
};
const LOGO_PULSE_TRANSITION = {
  duration: 1.6,
  repeat: Number.POSITIVE_INFINITY,
  ease: "easeInOut"
} as const;
const LOGO_REST_TRANSITION = {
  duration: 0.2,
  ease: "easeOut"
} as const;
const LABEL_ENTER = {
  opacity: 0,
  y: 4
};
const LABEL_ACTIVE = {
  opacity: 1,
  y: 0
};
const LABEL_EXIT = {
  opacity: 0,
  y: -4
};
const LABEL_TRANSITION = {
  duration: 0.18,
  ease: "easeOut"
} as const;
const ROLLING_ITEM_ENTER = {
  opacity: 0,
  y: 12
};
const ROLLING_ITEM_ACTIVE = {
  opacity: 1,
  y: 0
};
const ROLLING_ITEM_EXIT = {
  opacity: 0,
  y: -16,
  transition: {
    duration: 0.2,
    ease: "easeIn" as const
  }
};

// Sweeps a brighter foreground band across a muted base. Brighter than the
// shared <Shimmer> (which uses --color-background) so it actually reads as
// motion on a header.
const WORKING_SHIMMER_TRANSITION = {
  repeat: Number.POSITIVE_INFINITY,
  duration: 1.6,
  ease: "linear"
} as const;
const WORKING_SHIMMER_STYLE = {
  backgroundImage: "linear-gradient(90deg, var(--color-muted-foreground) 0%, var(--color-muted-foreground) 35%, var(--color-foreground) 50%, var(--color-muted-foreground) 65%, var(--color-muted-foreground) 100%)",
  backgroundSize: "250% 100%",
  backgroundRepeat: "no-repeat"
} as const;
const WorkingShimmer = memo(function WorkingShimmer() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      backgroundPosition: "0% 50%"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <motion.span animate={t0} className="bg-clip-text text-transparent text-xs" initial={{
      backgroundPosition: "100% 50%"
    }} style={WORKING_SHIMMER_STYLE} transition={WORKING_SHIMMER_TRANSITION}>Working</motion.span>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
});
type CollapsibleToolGroupProps = ToolTimelineProps & {
  /** Host registry, consulted for category-driven summaries. */
  registry: ToolUIRegistry;
};
export function CollapsibleToolGroup(t0) {
  const $ = _c(33);
  const {
    items,
    isStreamActive,
    registry
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== isStreamActive || $[1] !== items) {
    let t2;
    if ($[3] !== isStreamActive) {
      t2 = part => !(isPendingToolPart(part) && !isStreamActive);
      $[3] = isStreamActive;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = items.filter(_temp2).map(_temp3).filter(t2);
    $[0] = isStreamActive;
    $[1] = items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const completedParts = t1;
  let t2;
  if ($[5] !== completedParts || $[6] !== registry) {
    t2 = completedParts.length > 0 ? summarizeToolGroup(completedParts, registry) : "Stopped";
    $[5] = completedParts;
    $[6] = registry;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const summary = t2;
  let t3;
  if ($[8] !== items) {
    t3 = items.slice(-STACK_SIZE);
    $[8] = items;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const stackItems = t3;
  if (items.length === 0) {
    return null;
  }
  let t4;
  if ($[10] !== isStreamActive || $[11] !== items) {
    t4 = !isStreamActive && items.some(_temp4);
    $[10] = isStreamActive;
    $[11] = items;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const hasCancelledParts = t4;
  const showPreview = !open && (isStreamActive || hasCancelledParts);
  let t5;
  if ($[13] !== isStreamActive || $[14] !== items.length || $[15] !== open || $[16] !== summary) {
    t5 = <ToolGroupHeader isStreamActive={isStreamActive} open={open} summary={summary} total={items.length} />;
    $[13] = isStreamActive;
    $[14] = items.length;
    $[15] = open;
    $[16] = summary;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== isStreamActive || $[19] !== showPreview || $[20] !== stackItems) {
    t6 = showPreview && <motion.div animate={{
      height: "auto",
      opacity: 1
    }} className="relative overflow-hidden" exit={{
      height: 0,
      opacity: 0
    }} initial={{
      height: 0,
      opacity: 0
    }} key="rolling-preview" transition={PREVIEW_TRANSITION}><RollingStack isStreamActive={isStreamActive} stackItems={stackItems} /></motion.div>;
    $[18] = isStreamActive;
    $[19] = showPreview;
    $[20] = stackItems;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t6) {
    t7 = <AnimatePresence initial={false}>{t6}</AnimatePresence>;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Connector />;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== isStreamActive || $[26] !== items) {
    t9 = <CollapsibleContent>{t8}<ToolTimeline isStreamActive={isStreamActive} items={items} /></CollapsibleContent>;
    $[25] = isStreamActive;
    $[26] = items;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== open || $[29] !== t5 || $[30] !== t7 || $[31] !== t9) {
    t10 = <div className="my-1 min-w-0"><Collapsible onOpenChange={setOpen} open={open}>{t5}{t7}{t9}</Collapsible></div>;
    $[28] = open;
    $[29] = t5;
    $[30] = t7;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function _temp4(item_1) {
  return item_1.kind === "tool" && isPendingToolPart(item_1.part);
}
function _temp3(item_0) {
  return item_0.part;
}
function _temp2(item) {
  return item.kind === "tool";
}
type ToolGroupHeaderProps = {
  isStreamActive: boolean;
  open: boolean;
  summary: string;
  total: number;
};
function ToolGroupHeader(t0) {
  const $ = _c(25);
  const {
    isStreamActive,
    open,
    summary,
    total
  } = t0;
  const t1 = open ? "Collapse tool calls" : `Expand ${total} tool calls`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("group/trigger flex h-7 w-full min-w-0 cursor-pointer items-center", "text-muted-foreground transition-colors hover:text-foreground");
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== isStreamActive) {
    t3 = isStreamActive ? LOGO_PULSE : {
      scale: 1,
      opacity: 1
    };
    $[1] = isStreamActive;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = isStreamActive ? LOGO_PULSE_TRANSITION : LOGO_REST_TRANSITION;
  const t5 = isStreamActive ? "text-foreground" : "text-muted-foreground/70 group-hover/trigger:text-muted-foreground";
  let t6;
  if ($[3] !== t5) {
    t6 = cn("size-4 transition-colors duration-300", t5);
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t6) {
    t7 = <MagnusLogo aria-hidden={true} className={t6} />;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t3 || $[8] !== t4 || $[9] !== t7) {
    t8 = <div className="flex w-5 shrink-0 items-center justify-center"><motion.span animate={t3} className="inline-flex" transition={t4}>{t7}</motion.span></div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isStreamActive || $[12] !== summary) {
    t9 = <div className="relative min-w-0 overflow-hidden text-left"><AnimatePresence initial={false} mode="wait">{isStreamActive ? <motion.span animate={LABEL_ACTIVE} className="inline-flex items-center" exit={LABEL_EXIT} initial={LABEL_ENTER} key="working-label" transition={LABEL_TRANSITION}><WorkingShimmer /></motion.span> : <motion.span animate={LABEL_ACTIVE} className="block truncate text-xs" exit={LABEL_EXIT} initial={LABEL_ENTER} key="summary-label" transition={LABEL_TRANSITION}>{summary}</motion.span>}</AnimatePresence></div>;
    $[11] = isStreamActive;
    $[12] = summary;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = open ? "opacity-100" : "opacity-50 group-hover/trigger:opacity-100";
  let t11;
  if ($[14] !== t10) {
    t11 = cn("h-3 w-3 shrink-0 self-center transition-opacity", t10);
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11) {
    t12 = <ChevronsUpDown aria-hidden={true} className={t11} />;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t9) {
    t13 = <div className="flex min-w-0 items-center gap-1.5 pl-1.5">{t9}{t12}</div>;
    $[18] = t12;
    $[19] = t9;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t1 || $[22] !== t13 || $[23] !== t8) {
    t14 = <CollapsibleTrigger aria-label={t1} className={t2}>{t8}{t13}</CollapsibleTrigger>;
    $[21] = t1;
    $[22] = t13;
    $[23] = t8;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  return t14;
}

/**
 * Stack of the most recent N tool calls. New tools enter at the bottom;
 * once the stack overflows STACK_SIZE the oldest fades out the top. The
 * top connector lives inside the motion item so it travels with the row
 * during popLayout exits (no orphan lines from absolutely-positioned clones).
 */
const RollingStack = memo(function RollingStack(t0) {
  const $ = _c(8);
  const {
    isStreamActive,
    stackItems
  } = t0;
  let t1;
  if ($[0] !== isStreamActive || $[1] !== stackItems) {
    let t2;
    if ($[3] !== isStreamActive || $[4] !== stackItems.length) {
      t2 = (item, i) => <motion.div animate={ROLLING_ITEM_ACTIVE} exit={ROLLING_ITEM_EXIT} initial={ROLLING_ITEM_ENTER} key={item.index} layout={true} transition={STACK_TRANSITION}><Connector /><TimelineContext.Provider value={{
          isInTimeline: true,
          isLast: i === stackItems.length - 1,
          register: NOOP
        }}><TimelineItem isStreamActive={isStreamActive} item={item} /></TimelineContext.Provider></motion.div>;
      $[3] = isStreamActive;
      $[4] = stackItems.length;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = stackItems.map(t2);
    $[0] = isStreamActive;
    $[1] = stackItems;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <div className="relative"><AnimatePresence initial={false} mode="popLayout">{t1}</AnimatePresence></div>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
});

/**
 * Standalone "Thinking..." block used when a message only reasons before
 * answering (no tool calls to fold it into). Bare `Reasoning` — no lightbulb
 * gutter or connector spine, since there is no chain to thread it through;
 * `ReasoningTimelineItem` is the in-chain variant that adds those. The
 * `isStreaming` gate intentionally requires BOTH the part's own `streaming`
 * state AND the parent stream still being active, so a mid-reasoning failure
 * doesn't leave the shimmer spinning forever.
 */
export function StandaloneReasoning(t0) {
  const $ = _c(10);
  const {
    part,
    isStreamActive
  } = t0;
  let t1;
  if ($[0] !== part.text) {
    t1 = part.text.trim();
    $[0] = part.text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasText = !!t1;
  const t2 = isStreamActive && (part as {
    state?: string;
  }).state === "streaming";
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ReasoningTrigger />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== hasText || $[4] !== part.text) {
    t4 = hasText && <ReasoningContent>{part.text}</ReasoningContent>;
    $[3] = hasText;
    $[4] = part.text;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== hasText || $[7] !== t2 || $[8] !== t4) {
    t5 = <Reasoning defaultOpen={hasText} hasContent={hasText} isStreaming={t2}>{t3}{t4}</Reasoning>;
    $[6] = hasText;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}